import { useMutation } from '@apollo/client';
import type {
  UpdateAssessmentInput,
  UpdateOversightAssessmentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Assessment_Status_Enum,
  UpdateOversightAssessmentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateOversightAssessmentTRPC } from './useUpdateOversightAssessmentTRPC';

export const useUpdateOversightAssessment = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateOversightAssessmentGraphQL, graphqlState] = useMutation(
    UpdateOversightAssessmentDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk_assessment_result');
        evictField(cache, 'document_assessment_result');
        evictField(cache, 'obligation_assessment_result');
        evictField(cache, 'oversight_assessment');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateOversightAssessmentTRPC();

  const updateOversightAssessment = async (
    variables: UpdateAssessmentInput
  ): Promise<UpdateOversightAssessmentMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateOversightAssessment({
        ...variables,
        Title: variables.Title ?? '',
        Status: variables.Status ?? Assessment_Status_Enum.Notstarted,
      });
    }

    const result = await updateOversightAssessmentGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to update oversight assessment');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateOversightAssessment,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateOversightAssessment,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
